import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Image as ImageIcon, Play } from 'lucide-react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../../contexts/AuthContext';
import { generateImageId } from '../../../utils/crypto';
import { getAnonymousUserId } from '../../../utils/anonymousUser';
import { getApiErrorMessage } from '../../../utils/errorHandling';
import { processImageForUpload } from '../../../utils/imageProcessing';
import toast from 'react-hot-toast';

export const UploadZone = ({
  appState,
  uploadedFile,
  preview,
  onUploadStarted,
  onProcessingStarted,
  onChangePhoto
}) => {
  const { user, requireAuth, getIdToken } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError(null);

    if (rejectedFiles && rejectedFiles.length > 0) {
      const rejectedFile = rejectedFiles[0];
      const fileExt = rejectedFile.file.name.split('.').pop().toLowerCase();
      const errorMsg = `File format .${fileExt} is not supported. Please upload a JPEG, PNG, or WebP image.`;
      console.error(`[UploadZone] File rejected: ${rejectedFile.file.name}`);
      setError(errorMsg);
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;

    console.log(`[UploadZone] File accepted: ${file.name}`);

    // Create preview immediately (in-progress job check moved to submit time)
    const reader = new FileReader();
    reader.onload = () => {
      console.log(`[UploadZone] Preview created for ${file.name}`);
      onUploadStarted(file, reader.result);
    };
    reader.onerror = (error) => {
      console.error(`[UploadZone] Failed to create preview:`, error);
    };
    reader.readAsDataURL(file);
  }, [onUploadStarted]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    multiple: false
  });

  const handleSubmit = async () => {
    if (!uploadedFile) {
      console.warn('[UploadZone] Submit attempted with no file');
      return;
    }

    const processUpload = async () => {
      console.log(`[UploadZone] Starting upload process`);
      setIsUploading(true);

      try {
        const token = user ? await getIdToken() : null;

        if (!user) {
          throw new Error('Authentication required. Please sign in to upload images.');
        }

        const jobId = uuidv4();
        console.log(`[UploadZone] Job ID: ${jobId}`);

        // Process image (resize if needed) before upload
        console.log(`[UploadZone] Processing image (resizing if needed)...`);
        const processedFile = await processImageForUpload(uploadedFile);
        console.log(`[UploadZone] Image processing complete`);

        // Compute image hash
        const imageHash = await generateImageId(processedFile);
        console.log(`[UploadZone] Image hash: ${imageHash}`);

        const authHeaders = {
          'X-Frontend-Client': 'LaLala-Web',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };

        // Get signed URL (includes duplicate check)
        const requestBody = {
          fileName: processedFile.name,
          contentType: processedFile.type,
          jobId: jobId,
          imageHash: imageHash,
          anonymousUserId: getAnonymousUserId()
        };

        const signedUrlResponse = await axios.post(
          '/api/v1/upload/signed-url',
          requestBody,
          { headers: authHeaders }
        );

        // Handle in-progress job (one job at a time per user)
        if (signedUrlResponse.data.hasInProgressJob) {
          console.log(`[UploadZone] User has in-progress job: ${signedUrlResponse.data.existingJobId}`);
          toast('You have an in-progress job. Redirecting...', { icon: '⏳' });
          onProcessingStarted(signedUrlResponse.data.existingJobId);
          return;
        }

        // Handle duplicate image detection
        if (signedUrlResponse.data.isDuplicate) {
          console.log(`[UploadZone] Duplicate detected: ${signedUrlResponse.data.existingJobId}`);
          onProcessingStarted(signedUrlResponse.data.existingJobId);
          return;
        }

        const { signedUrl, fileUrl } = signedUrlResponse.data;
        console.log(`[UploadZone] Signed URL obtained`);

        // Upload to GCS
        await axios.put(signedUrl, processedFile, {
          headers: { 'Content-Type': processedFile.type },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`[UploadZone] Upload progress: ${percent}%`);
          }
        });
        console.log(`[UploadZone] File uploaded to GCS`);

        // Process image
        const processResponse = await axios.post(
          '/api/v1/upload/process-from-url',
          { imageUrl: fileUrl, jobId: jobId },
          { headers: authHeaders }
        );

        console.log(`[UploadZone] Processing initiated: ${processResponse.data.job_id}`);
        onProcessingStarted(processResponse.data.job_id);

      } catch (error) {
        console.error('[UploadZone] Upload failed:', error);
        const errorMessage = getApiErrorMessage(error, 'Error uploading image. Please try again.');
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsUploading(false);
      }
    };

    requireAuth(processUpload);
  };

  // Show preview state
  if (appState === 'preview') {
    return (
      <div className="text-center">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
            LaLala Model Maker
          </h1>
          <p className="text-lg sm:text-xl text-gray-600">
            Ready to create your personalized figure!
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 space-y-4 sm:space-y-6">
          {/* Large Preview */}
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="max-w-full h-64 sm:h-96 object-cover rounded-xl mx-auto"
            />
          </div>

          {/* Large Create Button */}
          <button
            onClick={handleSubmit}
            disabled={isUploading}
            className={`w-full py-4 sm:py-6 text-lg sm:text-2xl font-bold text-white rounded-2xl shadow-2xl
              transition-all transform hover:scale-105
              ${isUploading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600'
              }`}
          >
            {isUploading ? (
              <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-white"></div>
                <span>Processing...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                <Play className="w-6 h-6 sm:w-8 sm:h-8" />
                <span>🪄 Create My Model!</span>
              </div>
            )}
          </button>

          {/* Change Photo Link */}
          <button
            onClick={onChangePhoto}
            className="text-gray-500 text-sm underline hover:text-gray-700"
          >
            Change Photo
          </button>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show upload state (initial)
  return (
    <div className="text-center">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
          🎨 LaLala Model Maker
        </h1>
        <p className="text-lg sm:text-xl text-gray-600">
          Upload a photo and we'll create a custom figure for you!
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8">
        {/* Upload Area */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-2xl p-8 sm:p-16 cursor-pointer transition-all
            ${isDragActive
              ? 'border-orange-500 bg-orange-50'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }
          `}
        >
          <input {...getInputProps()} />

          <div className="space-y-4 sm:space-y-6">
            <Upload className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400 mx-auto" />
            <div>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                {isDragActive ? '📸 Drop it here!' : '📸 Upload Your Photo'}
              </p>
              <p className="text-base sm:text-lg text-gray-500">
                Tap here or drag & drop
              </p>
            </div>
            <p className="text-xs sm:text-sm text-gray-400">
              Supports JPG, PNG, WebP up to 10MB
            </p>
          </div>
        </div>

        {/* Compact Steps Row - Responsive */}
        <div className="mt-8 bg-gradient-to-r from-orange-50 via-purple-50 to-blue-50 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 md:space-x-8">
            {/* Step 1 */}
            <div className="flex items-center space-x-2">
              <span className="text-3xl">📸</span>
              <div>
                <div className="text-xs font-bold text-gray-500">STEP 1</div>
                <div className="text-sm font-bold text-gray-900">Upload Photo</div>
              </div>
            </div>

            {/* Arrow - hide on mobile */}
            <div className="hidden sm:block text-2xl text-gray-400">→</div>
            <div className="sm:hidden text-2xl text-gray-400">↓</div>

            {/* Step 2 */}
            <div className="flex items-center space-x-2">
              <span className="text-3xl">✨</span>
              <div>
                <div className="text-xs font-bold text-gray-500">STEP 2</div>
                <div className="text-sm font-bold text-gray-900">See Your Model</div>
              </div>
            </div>

            {/* Arrow - hide on mobile */}
            <div className="hidden sm:block text-2xl text-gray-400">→</div>
            <div className="sm:hidden text-2xl text-gray-400">↓</div>

            {/* Step 3 */}
            <div className="flex items-center space-x-2">
              <span className="text-3xl">🎁</span>
              <div>
                <div className="text-xs font-bold text-gray-500">STEP 3</div>
                <div className="text-sm font-bold text-gray-900">Order & Enjoy!</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
